import { setAuthSessionItem } from "utils/authSession";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appConstants } from "utils/appConstants";
import { LuLandmark, LuX } from "react-icons/lu";
import AddInvoice from "./AddInvoice";
import "./invoiceCreationGate.css";

const hasBankingDetails = (profile) =>
  [profile?.bankAccountName, profile?.bankBsbCode, profile?.bankAccountNumber].every(
    (value) => String(value || "").trim()
  );

export default function InvoiceCreationGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const addBankingButtonRef = useRef(null);
  const [gateState, setGateState] = useState("checking");

  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}");
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const rawUserId = sessionStorage.getItem("USER_ID");
    const userId =
      rawUserId && rawUserId !== "null" && rawUserId !== "undefined"
        ? rawUserId.replace(/["']/g, "").trim()
        : user._id;

    if (!userId) {
      setGateState("blocked");
      return undefined;
    }

    axios
      .post(
        `${appConstants.baseURL}user/fetchUserProfile`,
        { _id: userId },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
        }
      )
      .then(({ data }) => {
        if (cancelled) return;
        const profile = Array.isArray(data?.Result_Output)
          ? data.Result_Output[0]
          : data?.Result_Output;
        if (profile) {
          setAuthSessionItem(
            "LOGIN_DETAILS",
            JSON.stringify({ ...user, ...profile })
          );
        }
        setGateState(hasBankingDetails(profile) ? "allowed" : "blocked");
      })
      .catch(() => {
        if (!cancelled) setGateState("blocked");
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (gateState !== "blocked") return undefined;
    addBankingButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") navigate("/invoices", { replace: true });
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [gateState, navigate]);

  const returnTo = `${location.pathname}${location.search}`;
  return (
    <>
      <AddInvoice />
      {gateState === "checking" && (
        <div
          className="invoice-banking-gate-overlay invoice-banking-gate-overlay--checking"
          aria-label="Checking banking details"
          aria-busy="true"
        />
      )}
      {gateState === "blocked" && (
        <div className="invoice-banking-gate-overlay">
          <section
            className="invoice-banking-gate-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-banking-gate-title"
            aria-describedby="invoice-banking-gate-description"
          >
            <header>
              <LuLandmark aria-hidden="true" />
              <h2 id="invoice-banking-gate-title">
                Add bank details to create an invoice
              </h2>
              <button
                type="button"
                className="invoice-banking-gate-close"
                aria-label="Close"
                onClick={() => navigate("/invoices", { replace: true })}
              >
                <LuX />
              </button>
            </header>
            <div className="invoice-banking-gate-copy">
              <p id="invoice-banking-gate-description">
                Add your bank details before creating an invoice. This lets you
                receive payments from your customers.
              </p>
            </div>
            <div className="invoice-banking-gate-actions">
              <button
                type="button"
                className="invoice-banking-gate-secondary"
                onClick={() => navigate("/invoices", { replace: true })}
              >
                Not now
              </button>
              <button
                ref={addBankingButtonRef}
                type="button"
                className="invoice-banking-gate-primary"
                onClick={() =>
                  navigate(
                    `/settings?tab=payment&edit=bank&returnTo=${encodeURIComponent(
                      returnTo
                    )}`
                  )
                }
              >
                Add banking details
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
